import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Search, X } from 'lucide-react'
import { ChatList, ChatRoom } from '@/components/chat'
import { NewChatDialog } from '@/components/chat/NewChatDialog'
import { useChats } from '@/hooks/useChat'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'

/**
 * Chat Page - main chat interface.
 *
 * Responsive design:
 * - Desktop: Split view with chat list on left, chat room on right
 * - Mobile: Either chat list OR chat room (not both)
 *
 * Auto-selects first chat when none is selected.
 */
export function ChatPage() {
  const { roomId } = useParams()
  const navigate = useNavigate()
  const { chatListCollapsed, setChatListCollapsed } = useUIStore()
  const { data: chats } = useChats()
  const [isNewChatOpen, setIsNewChatOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // When navigating directly to a chat URL, show the chat room (not the list)
  useEffect(() => {
    if (roomId && !chatListCollapsed) {
      setChatListCollapsed(true)
    }
  }, [roomId, chatListCollapsed, setChatListCollapsed])

  // Auto-select first chat when none is selected (desktop only)
  useEffect(() => {
    // Only auto-select on desktop (lg breakpoint = 1024px)
    const isDesktop = window.innerWidth >= 1024

    if (!roomId && isDesktop && chats && chats.length > 0) {
      // Navigate to the first chat (most recent activity)
      const sortedChats = [...chats].sort((a, b) => {
        const aDate = a.lastMessage?.dateSent || a.dateCreated
        const bDate = b.lastMessage?.dateSent || b.dateCreated
        return new Date(bDate).getTime() - new Date(aDate).getTime()
      })
      navigate(`/chat/${sortedChats[0].id}`, { replace: true })
    }
  }, [roomId, chats, navigate])

  // Handle chat selection
  const handleChatSelect = useCallback(
    (chatId: string) => {
      navigate(`/chat/${chatId}`)
      setChatListCollapsed(true) // Collapse list on mobile after selection
    },
    [navigate, setChatListCollapsed]
  )

  // Handle back to list (mobile)
  const handleBack = useCallback(() => {
    setChatListCollapsed(false)
    navigate('/chat')
  }, [navigate, setChatListCollapsed])

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {/* Chat list sidebar */}
      <aside
        className={cn(
          'flex min-h-0 flex-col border-r border-gray-200 bg-white',
          // On mobile: full width when no chat selected, hidden when chat selected
          'w-full lg:w-80 lg:flex-shrink-0',
          // Mobile visibility
          roomId && chatListCollapsed ? 'hidden lg:flex' : 'flex'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h1 className="text-xl font-semibold text-text-dark">Chats</h1>
          <button
            onClick={() => setIsNewChatOpen(true)}
            className="rounded-full bg-primary p-2 text-white shadow-sm hover:bg-primary-dark"
            title="New chat"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>

        {/* Search input */}
        <div className="border-b border-gray-200 p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search chats..."
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-9 text-sm placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto">
          <ChatList onChatSelect={handleChatSelect} searchTerm={searchTerm} />
        </div>
      </aside>

      {/* Chat room */}
      <main
        className={cn(
          'flex flex-1 flex-col bg-white',
          // Mobile visibility
          !roomId || !chatListCollapsed ? 'hidden lg:flex' : 'flex'
        )}
      >
        <ChatRoom onBack={handleBack} />
      </main>

      {/* New Chat Dialog */}
      <NewChatDialog isOpen={isNewChatOpen} onClose={() => setIsNewChatOpen(false)} />
    </div>
  )
}
